// components/teams/detailCards/members.jsx
import React, { useEffect, useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import { startDirectMessage } from '../../../utils/messages';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { createNotification } from '../../../utils/createNotification';
import EmergencyContactsModal from '../components/modals/EmergencyContactsModal';
import { ConfirmAccessModal } from '../components/modals/ConfirmAccessModal';
import '../../../styles/teamCards.css';

const MEMBERS_PAGE_SIZE = 5;

export default function MemberListCard({
  members = [],
  isAdmin = false,
  canImportMembers = isAdmin,
  canExportMembers = isAdmin,
  onEdit,
  onOpenMemberProfile,
  enableMessages = false,
  canEmailMembers = false,
  onEmailMember,
  onImportMembersClick,
  onInviteMembersClick,
  onExportMembersClick,
  profileFieldsEnabled = false,
  showProfileSearchButton = false,
  onOpenProfileSearch,
  teamId // REQUIRED for emergency contacts + team name
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const addMembersId = useId();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [selectedMember, setSelectedMember] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [resolvedContacts, setResolvedContacts] = useState([]);
  const [resolvedMemberPhone, setResolvedMemberPhone] = useState('');
  const filteredMembers = useMemo(() => {
    const searchNeedle = search.trim().toLowerCase();
    if (!searchNeedle) return members;
    return members.filter(member => String(member?.name || '').toLowerCase().includes(searchNeedle));
  }, [members, search]);
  const trimmedSearch = search.trim();
  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / MEMBERS_PAGE_SIZE));
  const pageStart = (page - 1) * MEMBERS_PAGE_SIZE;
  const visibleMembers = useMemo(() => filteredMembers.slice(pageStart, pageStart + MEMBERS_PAGE_SIZE), [filteredMembers, pageStart]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleMessageClick = async member => {
    try {
      const thread = await startDirectMessage(user, member, teamId);
      navigate(`/messages/${thread.id}`);
    } catch (err) {
      alert('Could not start conversation.');
    }
  };

  // Always fetch emergency contacts from teams/{teamId}/members/{memberId}.
  // Member phone is best-effort because private profile reads may be restricted.
  const fetchEmergencyDetails = async member => {
    const memberId = member?.id;
    if (!teamId || !memberId) return { contacts: [], memberPhone: '' };
    try {
      const ref = doc(db, 'teams', teamId, 'members', memberId);
      const snap = await getDoc(ref);
      const memberData = snap.exists() ? snap.data() || {} : {};
      let memberPhone = String(
        member?.phone
        || memberData?.phone
        || memberData?.profilePhone
        || memberData?.profileFields?.phone
        || memberData?.profileFields?.phoneNumber
        || ''
      ).trim();

      if (!memberPhone) {
        const privateSnap = await getDoc(doc(db, 'privateUsers', memberId)).catch(() => null);
        memberPhone = privateSnap?.exists() ? String(privateSnap.data()?.phone || '').trim() : '';
      }

      return {
        contacts: Array.isArray(memberData?.emergencyContacts) ? memberData.emergencyContacts : [],
        memberPhone,
      };
    } catch (e) {
      return { contacts: [], memberPhone: '' };
    }
  };
  const getAdminDisplayName = async uid => {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      const u = snap.exists() ? snap.data() : null;
      // fields from your screenshot: firstName, lastName, username
      if (u?.firstName || u?.lastName) return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
      return u?.username || 'An admin';
    } catch {
      return 'An admin';
    }
  };
  const getTeamName = async teamId => {
    if (!teamId) return 'your club';
    try {
      const snap = await getDoc(doc(db, 'teams', teamId));
      return snap.exists() ? snap.data()?.name || 'your club' : 'your club';
    } catch {
      return 'your club';
    }
  };
  const handleOpenEmergency = member => {
    setSelectedMember(member);
    setShowConfirm(true);
  };
  const confirmAndOpen = async () => {
    if (!selectedMember) return;
    setContactsLoading(true);

    // Resolve names for the notification
    const [adminName, teamName] = await Promise.all([getAdminDisplayName(user?.uid), getTeamName(teamId)]);

    // 1) Notify the member
    try {
      await createNotification({
        userId: selectedMember.id,
        message: `${adminName} from ${teamName} accessed your emergency contact information.`,
        actionUrl: '',
        teamId,
        notificationCategory: 'family_profile',
        pushContext: teamName,
      });
    } catch (e) {}

    // 2) Fetch contacts and open modal
    const { contacts, memberPhone } = await fetchEmergencyDetails(selectedMember);
    setResolvedContacts(contacts);
    setResolvedMemberPhone(memberPhone);
    setShowConfirm(false);
    setShowContacts(true);
    setContactsLoading(false);
  };
  return <div className="team-card members-card">
      <div className="card-header-with-icon" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
        <span className="card-title">Members ({members.length})</span>
        {isAdmin && showProfileSearchButton && <button type="button" onClick={e => {
        e.stopPropagation();
        onOpenProfileSearch?.();
      }} aria-label="Open profile search" title="Open profile search" className="edit-btn">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 2a8 8 0 105.293 14.293l5.207 5.207a1 1 0 001.414-1.414l-5.207-5.207A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" />
            </svg>
          </button>}
      </div>

      <div className="card-body members-card__body">
        <div className="members-card__toolbar">
          <input type="text" className="input members-card__search" aria-label="Search members by name" placeholder="Search members by name..." value={search} onChange={e => {
          setSearch(e.target.value);
          setPage(1);
        }} />

          {(canImportMembers || canExportMembers) && <>
              {canImportMembers && <button onClick={e => {
            e.stopPropagation();
            setShowAddMembers(current => !current);
          }} className="btn btn--primary btn--sm" aria-expanded={showAddMembers} aria-controls={addMembersId} type="button">
                <span aria-hidden>➕</span>
                <span>Add members</span>
              </button>}
              {canExportMembers && <button onClick={e => {
            e.stopPropagation();
            onExportMembersClick?.(e);
          }} className="btn btn--secondary btn--sm" aria-label="Export members" title="Export members" type="button">
                <span aria-hidden>⬇️</span>
                <span>Export</span>
              </button>}
            </>}
        </div>

        {canImportMembers && showAddMembers && <div id={addMembersId} className="members-card__add-options" role="group" aria-label="Ways to add members">
          <button type="button" onClick={event => {
            event.stopPropagation();
            setShowAddMembers(false);
            onInviteMembersClick?.(event);
          }}><strong>Invite by email</strong><span>Send a joining invitation. Members complete their own details.</span></button>
          <button type="button" onClick={event => {
            event.stopPropagation();
            setShowAddMembers(false);
            onImportMembersClick?.(event);
          }}><strong>Import existing members</strong><span>Upload a CSV with member details, membership options and paid-through dates, then send claim invitations.</span></button>
        </div>}

        <div className="text-muted" style={{
        fontSize: '0.85rem',
        marginBottom: '0.5rem'
      }}>
          {filteredMembers.length === 0 ? trimmedSearch ? `No members match "${trimmedSearch}".` : 'No members yet.' : `${pageStart + 1}-${Math.min(pageStart + MEMBERS_PAGE_SIZE, filteredMembers.length)} of ${filteredMembers.length}${trimmedSearch ? ` matching members (${members.length} total)` : ' members'}`}
        </div>

        <ul className="list-none divide-y">
          {visibleMembers.map(member => <li key={member.id} className="member-list-item">
              <span className="member-name">{member.name}</span>
              {(isAdmin || enableMessages || canEmailMembers) && <div className="member-actions" style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}>
                  {isAdmin && profileFieldsEnabled && <button onClick={event => {
                event.stopPropagation();
                (onOpenMemberProfile || onEdit)?.(member);
              }} aria-label={`Edit profile details for ${member.name}`} title={`Edit profile details for ${member.name}`} className="btn btn--secondary btn--xs member-profile-edit-button" type="button">
                    Profile
                  </button>}

                  {/* Edit tags */}
                  {isAdmin && <button onClick={event => {
                event.stopPropagation();
                onEdit?.(member);
              }} aria-label={`Edit tags for ${member.name}`} title={`Edit tags for ${member.name}`} className="tag-edit-button" type="button" style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer'
            }}>
	                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
	                      <path d="M20.59 13.41L10.41 3.22c-.78-.78-2.05-.78-2.83 0L3 8v8a2 2 0 002 2h8l4.59-4.59c.78-.78.78-2.05 0-2.83zM7 9.5A1.5 1.5 0 117 6.5a1.5 1.5 0 010 3z" />
	                    </svg>
	                  </button>}

	                  {/* Emergency contacts icon */}
	                  {isAdmin && <button onClick={event => {
	                event.stopPropagation();
	                handleOpenEmergency(member);
	              }} aria-label={`View ${member.name}'s emergency contacts`} title={`View ${member.name}'s emergency contacts`} className="emergency-contacts-button" type="button" style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer'
            }}>
	                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
	                      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 2a8 8 0 110 16 8 8 0 010-16zm1 11h-2v-6h2v6zm0 3h-2v-2h2v2z" />
	                    </svg>
	                  </button>}

                  {enableMessages && <button onClick={event => {
                event.stopPropagation();
                handleMessageClick(member);
              }} aria-label={`Message ${member.name}`} title={`Message ${member.name}`} className="message-button" type="button" style={{
              background: 'none',
              border: 'none',
              padding: 6,
              cursor: 'pointer'
            }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2 3h20v18l-4-4H2V3zm2 2v10h13.17L20 18.17V5H4z" />
                      </svg>
                    </button>}

                  {canEmailMembers && <button onClick={event => {
                event.stopPropagation();
                onEmailMember?.(member);
              }} aria-label={`Email ${member.name}`} title={`Email ${member.name}`} className="email-member-button" type="button" style={{
              background: 'none',
              border: 'none',
              padding: 6,
              cursor: 'pointer',
              position: 'relative',
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="5" width="18" height="14" rx="2.25" />
                      <path d="m4 7 8 6 8-6" />
                    </svg>
                    <span aria-hidden="true" style={{
                      position: 'absolute',
                      left: '50%',
                      top: '53%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: 10,
                      lineHeight: 1,
                      fontWeight: 800,
                      color: 'currentColor',
                      background: 'var(--surface, #fff)',
                      borderRadius: '50%',
                      padding: '0 1px'
                    }}>@</span>
                  </button>}
                </div>}
            </li>)}
        </ul>

        {filteredMembers.length > MEMBERS_PAGE_SIZE && <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 10
      }}>
            <span className="text-muted" style={{
          fontSize: '0.85rem'
        }}>
              Page {page} of {totalPages}
            </span>
            <div style={{
          display: 'flex',
          gap: 6
        }}>
              <button type="button" className="btn btn--secondary btn--xs" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                Prev
              </button>
              <button type="button" className="btn btn--secondary btn--xs" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                Next
              </button>
            </div>
          </div>}
      </div>

      {showConfirm && selectedMember && <ConfirmAccessModal memberName={selectedMember.name} onCancel={() => setShowConfirm(false)} onConfirm={confirmAndOpen} busy={contactsLoading} />}

      {showContacts && selectedMember && <EmergencyContactsModal memberName={selectedMember.name} memberPhone={resolvedMemberPhone} contacts={resolvedContacts} onClose={() => {
      setShowContacts(false);
      setSelectedMember(null);
      setResolvedMemberPhone('');
    }} />}
    </div>;
}
MemberListCard.propTypes = {
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  isAdmin: PropTypes.bool,
  canImportMembers: PropTypes.bool,
  canExportMembers: PropTypes.bool,
  onEdit: PropTypes.func,
  onOpenMemberProfile: PropTypes.func,
  enableMessages: PropTypes.bool,
  canEmailMembers: PropTypes.bool,
  onEmailMember: PropTypes.func,
  onImportMembersClick: PropTypes.func,
  onInviteMembersClick: PropTypes.func,
  onExportMembersClick: PropTypes.func,
  profileFieldsEnabled: PropTypes.bool,
  showProfileSearchButton: PropTypes.bool,
  onOpenProfileSearch: PropTypes.func,
  teamId: PropTypes.string.isRequired
};
